import React from "react"
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Switch,
  Icon,
  Select,
  message
} from "antd"
import PropTypes from "prop-types"
import {autobind} from "core-decorators"
import {mapPropsToFields} from "utils/form"
import ReactTelephoneInput from "react-telephone-input/lib/withStyles"
import createLanguage, {langPropTypes} from "hoc/create-lang"
import _ from "lodash"
import SelectTimezone, {
  getTimezoneInfo
} from "components/elements/select-timezone"
import momentTimezone from "moment-timezone"
import RetryIcon from '@atlaskit/icon/glyph/refresh';
import CopyIcon from '@atlaskit/icon/glyph/copy';
import styled from 'styled-components'
import {generateApiKey} from "../../../utils/auth"
import {copyTextToClipboard} from "../../../utils/form"

const FormItem = Form.Item
const {Option} = Select;

const GenerateAPIWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-left: 10px;
  color: rgb(51, 103, 214);
  cursor: pointer;
`
const APIKeyInput = styled(Input)`
  cursor: pointer;
`

@Form.create({
  mapPropsToFields: ({initialValues}) => {
    if (!initialValues) return
    if (initialValues.phone) {
      initialValues.phone = initialValues.phone.phoneNumber
    }
    return mapPropsToFields({initialValues})
  }
})
@createLanguage
@autobind
export default class OrganizationForm extends React.PureComponent {
  static propTypes = {
    onSubmit: PropTypes.func,
    lang: langPropTypes,
    isEdit: PropTypes.bool,
    initialValues: PropTypes.object
  }
  constructor(props) {
    super(props)

    const translate = this.props.lang.t
    this.i18n = {
      enableTimeline: translate("organization.form.timeline.label.enabled"),
      enableShareAPI: translate("organization.form.enableShareAPI"),
      apiKey: translate("organization.form.apiKey"),
      generateApiKey: translate("organization.form.generateApiKey"),
      regenerateApiKey: translate("organization.form.regenerateApiKey"),
      timelineKey: translate("organization.form.timeline.input.label.key"),
      timelineSecret: translate(
        "organization.form.timeline.input.label.secret"
      ),
      timelineAppId: translate("organization.form.timeline.input.label.appId")
    }

    this.state = {
      confirmDirty: false,
      phone: {},
      isCustom: false
    }
  }

  handleSubmit(e) {
    e.preventDefault()
    this.props.form.validateFields((err, values) => {
      const timeline = _.get(this.props.initialValues, "timeline")
      const databaseInfo = _.get(this.props.initialValues, "databaseInfo")
      if (err) return
      const data = {
        name: values.name,
        organizationType: values.organizationType,
        address: values.address,
        addressAPI: values.addressAPI,
        phone: this.state.phone,
        databaseInfo: {
          name: values.databaseName,
          address: values.databaseAddress,
          user: values.databaseUser,
          pwd: values.databasePwd,
          port: values.databasePort
        },
        timeline: {
          enabled: values.timelineEnabled,
          configs: {
            key: values.timelineKey,
            secret: values.timelineSecret,
            appId: values.timelineAppId
          }
        },
        timeZone: {
          ...getTimezoneInfo(values.timeZoneValue),
          utcOffset: momentTimezone.tz(values.timeZoneValue).format("Z")
        },

        email: values.email,
        enableShareAPI: values.enableShareAPI,
        apiKey: values.apiKey
      }

      if (!this.state.isCustom) {
        data.databaseInfo.address = _.get(databaseInfo, "address")
        data.databaseInfo.user = _.get(databaseInfo, "user")
        data.databaseInfo.pwd = _.get(databaseInfo, "pwd")
        data.databaseInfo.port = _.get(databaseInfo, "port")
      }

      if (!values.timelineEnabled) {
        data.timeline.configs.key = _.get(timeline, "configs.key")
        data.timeline.configs.secret = _.get(timeline, "configs.secret")
        data.timeline.configs.appId = _.get(timeline, "configs.appId")
      }

      // Callback submit form Container Component
      this.props.onSubmit(data)
    })
  }

  validateToNextPassword = (rule, value, callback) => {
    const form = this.props.form
    if (value && this.state.confirmDirty) {
      form.validateFields(["confirm"], {force: true})
    }
    callback()
  }

  compareToFirstPassword = (rule, value, callback) => {
    const form = this.props.form
    if (value && value !== form.getFieldValue("databasePwd")) {
      callback("Two passwords that you enter is inconsistent!")
    } else {
      callback()
    }
  }
  handleConfirmBlur = e => {
    const value = e.target.value
    this.setState({confirmDirty: this.state.confirmDirty || !!value})
  }

  handleTelChange(telNumber, selectedCountry) {
    this.setState({
      phone: {
        phoneNumber: telNumber,
        ...selectedCountry
      }
    })
  }

  handleCopyApiKey() {
    const {form} = this.props
    const apiKey = form.getFieldValue('apiKey')
    copyTextToClipboard(apiKey)
    message.success('Code copied to clipboard')
  }

  async handleGenerateToken () {
    this.setState({isGenerating: true})
    const apiKey = await generateApiKey()

    const {form} = this.props;
    form.setFieldsValue({apiKey})
    this.setState({isGenerating: false})
  }

  async componentWillMount() {
    if (this.props.isEdit) {
      this.setState({
        isCustom:
          this.props.initialValues &&
            this.props.initialValues.databaseInfo &&
            this.props.initialValues.databaseInfo.user
            ? true
            : false
      })
    }
  }

  render() {
    // console.log("this.props.initialValues", this.props.initialValues)
    const {form: {getFieldDecorator, getFieldValue}, lang: {t}, initialValues} = this.props
    // const { timeline, databaseInfo } = this.props.initialValues

    const timeline = _.get(this.props.initialValues, "timeline")
    const databaseInfo = _.get(this.props.initialValues, "databaseInfo")

    const {isGenerating} = this.state

    return (
      <Form onSubmit={this.handleSubmit}>
        <Row gutter={24}>
          <Col span={12}>
            <FormItem label={t("organization.form.name.label")}>
              {getFieldDecorator(`name`, {
                initialValue: this.props.initialValues
                  ? this.props.initialValues.name
                  : null,
                rules: [
                  {
                    required: true,
                    message: t("organization.form.name.label")
                  }
                ]
              })(
                <Input placeholder={t("organization.form.name.placeholder")} />
              )}
            </FormItem>
          </Col>
          <Col span={12}>
            <FormItem label={t("organization.form.address.label")}>
              {getFieldDecorator(`address`, {
                initialValue: this.props.initialValues
                  ? this.props.initialValues.address
                  : null
              })(
                <Input
                  placeholder={t("organization.form.address.placeholder")}
                />
              )}
            </FormItem>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col span={12}>
            <FormItem label={t("organization.form.phone.label")}>
              {getFieldDecorator(`phone`, {
                initialValue:
                  this.props.initialValues && this.props.initialValues.phone
                    ? this.props.initialValues.phone.phoneNumber
                    : null,
                rules: [
                  {
                    required: true,
                    message: t("organization.form.phone.label")
                  }
                ]
              })(
                <ReactTelephoneInput
                  defaultCountry="vn"
                  flagsImagePath={
                    !this.props.isEdit
                      ? "../images/flags.png"
                      : "../../images/flags.png"
                  }
                  onChange={this.handleTelChange}
                // onBlur={this.handleInputBlur}
                />
              )}
            </FormItem>
          </Col>
          <Col span={12}>
            <FormItem label={t("organization.form.addressAPI.label")}>
              {getFieldDecorator(`addressAPI`, {
                initialValue: this.props.initialValues
                  ? this.props.initialValues.addressAPI
                  : null
                // rules: [
                //   {
                //     required: true,
                //     message: t('userSearchFrom.form.addressAPI.label')
                //   }
                // ]
              })(
                <Input
                  placeholder={t("organization.form.addressAPI.placeholder")}
                />
              )}
            </FormItem>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col span={12}>
            <FormItem label={t("organization.form.timeZone.label")}>
              {getFieldDecorator(`timeZoneValue`, {
                initialValue: _.get(
                  this.props.initialValues,
                  "timeZone.value",
                  momentTimezone.tz.guess()
                ),
                rules: [
                  {
                    required: true,
                    message: t("organization.form.timeZone.label")
                  }
                ]
              })(<SelectTimezone />)}
            </FormItem>
          </Col>
          <Col span={6}>
            <FormItem label={t("organization.form.email.label")}>
              {getFieldDecorator(`email`, {
                rules: [
                  {
                    required: true,
                    message: 'email required'
                  },
                  {
                    type: "email",
                    message: 'invalid format'
                  }
                ]
              })(<Input placeholder={t("organization.form.email.placeholder")} style={{width: "100%"}} />)}
            </FormItem>
          </Col>
          <Col span={6}>
            <FormItem label={t("organization.form.organizationType.label")}>
              {getFieldDecorator(`organizationType`, {
                initialValue: _.get(this.props.initialValues, 'packageInfo.organizationType', ''),
                rules: [
                  {
                    required: true,
                    message: 'organizationType required'
                  }
                ]
              })(
                <Select style={{width: 200}} onChange={() => {}}>
                  <Option value="CLOUD">Clould</Option>
                  <Option value="ON_PREMISES">On-Premises</Option>
                </Select>
              )}
            </FormItem>
          </Col>

        </Row>
        <Row type="flex" gutter={24}>
          <Col span={12}>
            <div>
              <strong>Information Connections: </strong>
              <Switch
                checkedChildren="Custom"
                unCheckedChildren="Default"
                checked={this.state.isCustom}
                onChange={checked => {
                  this.setState({
                    isCustom: checked
                  })
                }}
              />
            </div>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col span={12}>
            <FormItem label={t("organization.form.databaseInfo.name.label")}>
              {getFieldDecorator(`databaseName`, {
                initialValue: _.get(databaseInfo, "name"),
                rules: [
                  {
                    required: true,
                    message: t("organization.form.databaseInfo.name.label")
                  },
                  {
                    validator: (rule, value, callback) => {
                      var format = /[ !@#$%^&*()+\-=[\]{};':"\\|,.<>/?]/
                      if (format.test(value))
                        callback(
                          new Error(
                            t("organization.form.databaseInfo.name.message")
                          )
                        )
                      callback()
                    }
                  }
                ]
              })(
                <Input
                  disabled={this.props.isEdit}
                  placeholder={t("organization.form.databaseInfo.name.label")}
                />
              )}
            </FormItem>
          </Col>

          {this.state.isCustom && (
            <Col span={12}>
              <FormItem
                label={t("organization.form.databaseInfo.address.label")}
              >
                {getFieldDecorator(`databaseAddress`, {
                  initialValue: _.get(databaseInfo, "address"),
                  rules: [
                    {
                      required: true,
                      message: t("organization.form.databaseInfo.address.label")
                    }
                  ]
                })(
                  <Input
                    placeholder={t(
                      "organization.form.databaseInfo.address.label"
                    )}
                  />
                )}
              </FormItem>
            </Col>
          )}
        </Row>

        {this.state.isCustom && (
          <Row gutter={24}>
            <Col span={12}>
              <FormItem label={t("organization.form.databaseInfo.user.label")}>
                {getFieldDecorator(`databaseUser`, {
                  initialValue: _.get(databaseInfo, "user"),
                  rules: [
                    {
                      required: true,
                      message: t("organization.form.databaseInfo.user.label")
                    }
                  ]
                })(
                  <Input
                    placeholder={t("organization.form.databaseInfo.user.label")}
                  />
                )}
              </FormItem>
            </Col>
            <Col span={12}>
              <FormItem label={t("organization.form.databaseInfo.port.label")}>
                {getFieldDecorator(`databasePort`, {
                  initialValue: _.get(databaseInfo, "port"),
                  rules: [
                    {
                      required: true,
                      message: t("organization.form.databaseInfo.port.label")
                    }
                  ]
                })(
                  <Input
                    placeholder={t(
                      "organization.form.databaseInfo.port.placeholder"
                    )}
                  />
                )}
              </FormItem>
            </Col>
          </Row>
        )}

        {this.state.isCustom && (
          <Row gutter={16}>
            <Col span={12}>
              <FormItem label={t("organization.form.databaseInfo.pwd.label")}>
                {getFieldDecorator("databasePwd", {
                  initialValue: _.get(databaseInfo, "pwd"),
                  rules: [
                    {
                      required: true,
                      message: t("organization.form.databaseInfo.pwd.label")
                    },
                    {
                      validator: this.validateToNextPassword
                    }
                  ]
                })(
                  <Input
                    type="password"
                    // disabled={this.props.isEdit}
                    placeholder={t("organization.form.databaseInfo.pwd.label")}
                  />
                )}
              </FormItem>
            </Col>
            <Col span={12}>
              <FormItem
                label={t(
                  "organization.form.databaseInfo.confirmPassword.label"
                )}
              >
                {getFieldDecorator("confirmPassword", {
                  initialValue: _.get(databaseInfo, "pwd"),
                  rules: [
                    {
                      required: true,
                      message: t(
                        "organization.form.databaseInfo.confirmPassword.message"
                      )
                    },
                    {
                      validator: this.compareToFirstPassword
                    }
                  ]
                })(
                  <Input
                    type="password"
                    placeholder={t(
                      "organization.form.databaseInfo.confirmPassword.label"
                    )}
                    onBlur={this.handleConfirmBlur}
                  />
                )}
              </FormItem>
            </Col>
          </Row>
        )}

        <Row style={{marginBottom: 30}}>
          <Col md={12}>
            <FormItem label={this.i18n.apiKey}>
              {getFieldDecorator("apiKey", {
                initialValue: _.get(initialValues, 'apiKey'),
              })(<APIKeyInput disabled suffix={<CopyIcon onClick={this.handleCopyApiKey} />} />)}
            </FormItem>
          </Col>
          <Col md={6}>
            <FormItem label=" " colon={false}>
              <GenerateAPIWrapper onClick={this.handleGenerateToken}>
                {
                  !isGenerating 
                  ? <RetryIcon />
                  : <Icon type="loading" style={{fontSize: 20}}/>
                }
                <span style={{marginLeft: 10}}>{getFieldValue("apiKey") ? this.i18n.regenerateApiKey : this.i18n.generateApiKey}</span>
              </GenerateAPIWrapper>
            </FormItem>
          </Col>
        </Row>
        {/* ---------- timeline form inputs ---------- */}
        <Row style={{marginBottom: 30}}>
          <Row>
            <Col md={6}>
              <strong>{this.i18n.enableTimeline}</strong>:&nbsp;&nbsp;
            {getFieldDecorator("timelineEnabled", {
                initialValue: _.get(timeline, "enabled"),
                valuePropName: "checked"
              })(
                <Switch
                  checkedChildren={<Icon type="check" />}
                  unCheckedChildren={<Icon type="close" />}
                />
              )}
            </Col>
          </Row>

          {this.props.form.getFieldValue("timelineEnabled") && (
            <Row>
              <FormItem label={this.i18n.timelineKey}>
                {getFieldDecorator("timelineKey", {
                  initialValue: _.get(timeline, "configs.key"),
                  rules: [{required: true}]
                })(<Input />)}
              </FormItem>
              <FormItem label={this.i18n.timelineSecret}>
                {getFieldDecorator("timelineSecret", {
                  initialValue: _.get(timeline, "configs.secret"),
                  rules: [{required: true}]
                })(<Input />)}
              </FormItem>
              <FormItem label={this.i18n.timelineAppId}>
                {getFieldDecorator("timelineAppId", {
                  initialValue: _.get(timeline, "configs.appId"),
                  rules: [{required: true}]
                })(<Input />)}
              </FormItem>
            </Row>
          )}
        </Row>
        <Row style={{marginBottom: 30}}>
          <Col md={6}>
            <strong>{this.i18n.enableShareAPI}</strong>:&nbsp;&nbsp;
            {getFieldDecorator("enableShareAPI", {
              initialValue: _.get(initialValues, 'enableShareAPI'),
              valuePropName: "checked"
            })(
              <Switch
                checkedChildren={<Icon type="check" />}
                unCheckedChildren={<Icon type="close" />}
              />
            )}
          </Col>
        </Row>
        <FormItem>
          <Button style={{width: "100%"}} type="primary" htmlType="submit">
            Save
          </Button>
        </FormItem>
      </Form>
    )
  }
}



// WEBPACK FOOTER //
// ./src/containers/organization/organization-form/index.js